// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

// Component dependencies.
import { HybridButton } from '../../../../common/components/buttons.jsx';

// Misc dependencies.
import { getLabel, capitalize } from '../../../../common/utils/string.js';

// Config.
import Config from '../../config/config.js';
import Enums from '../../config/enums.js';

/**
 * <pre>
 * UgcWall > Toolbars > Filtering Toolbar React component.
 *
 * Summary of responsibilities:
 *
 *  - outputs a toolbar to allow resultset to be filtered by various criteria (eg. free text entry username search, social network, content type).
 * </pre>
 *
 * @memberof module:UgcWall.Components.Elements.Toolbars
 * @namespace
 *
 * @return {module:UgcWall.Components.Elements.Toolbars.FilteringToolbar}
 */
class FilteringToolbar extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Any registered select/combo filters.
    this.selectFilters = [];

    // Timeouts for monitoring filter changes.
    this.timeouts = {};

    // Define a list of networks to output.
    this.networkConfig = this.buildNetworkConfig(props.contentServicesVersion);

    // Define a list of content types to output.
    this.contentTypeConfig = this.buildContentTypeConfig(props.contentServicesVersion);

    // Bind methods.
    this.handleButtonClicked = this.handleButtonClicked.bind(this);
    this.handleTextInputChangeBuffer = this.handleTextInputChangeBuffer.bind(this);
    this.handleTextInputChanged = this.handleTextInputChanged.bind(this);
    this.handleSelectChanged = this.handleSelectChanged.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Content Services related.
  buildNetworkConfig(contentServicesVersion) {
    // Define list of networks to output.
    const _networkList = [
      { value: 'twitter' },
      { value: 'facebook' },
      { value: 'instagram' },
      { value: 'youtube' },
      { value: 'flickr' },
      { value: 'ugc' },
    ];

    // Define config for different Content Services versions.
    const _contentServiceConfig = {
      v1: {
        prefix: 'external.service',
      },

      v2: {
        //prefix: 'type.stream'
        prefix: 'external.service',
      },
    };

    // Build network config.
    const _networkConfig = [{ value: 'all', label: getLabel('ugc.filters.network.all'), icon: '' }];
    for (const networkKey in _networkList) {
      const networkData = _networkList[networkKey];
      const networkValue = networkData.value;
      const networkLabel = networkData.label ? networkData.label : null;
      const networkIcon = networkData.icon ? networkData.icon : null;

      _networkConfig.push({
        value: _contentServiceConfig[contentServicesVersion].prefix + '.' + networkValue,
        label: networkLabel ? capitalize(networkLabel) : capitalize(networkValue),
        icon:  'xIcon' + (networkIcon ? capitalize(networkIcon) : capitalize(networkValue)),
      });
    }

    return _networkConfig;
  }

  buildContentTypeConfig(contentServicesVersion) {
    // Define config for different Content Services versions.
    const _contentServiceConfig = {
      v1: {
        types: [{ value: 'content.stream.media', label: 'Media' }, { value: 'content.stream.status', label: 'Status' }],
      },

      v2: {
        types: [
          { value: 'type.stream.source.social', label: 'Social' },
          { value: 'type.stream.source.ugc', label: 'Ugc' },
          { value: 'type.stream.source.seeded', label: 'Seeded' },
          { value: 'type.stream.source.injected', label: 'Injected' },
          { value: 'type.stream.source.enterprise', label: 'Enterprise' },
          { value: 'type.stream.source.api', label: 'Api' },
        ],
      },
    };

    // Build type config.
    const _typeConfig = [{ value: 'all', label: getLabel('ugc.filters.type.all') }];

    const _typeList = _contentServiceConfig[contentServicesVersion].types;

    for (const typeKey in _typeList) {
      const typeData = _typeList[typeKey];
      const typeValue = typeData.value;
      const typeLabel = typeData.label;

      _typeConfig.push({
        value: typeValue,
        label: typeLabel,
      });
    }

    return _typeConfig;
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Lifecycle methods.

  /**
   * Called when component mounts for the first time.
   * Initializes Selectize combo box if in drop down render mode.
   *
   */
  componentDidMount() {
    // Init any select/combo filters.
    this.selectFilters.map((filter, index) => {
      const filterRef = filter.ref;
      const filterType = filter.type;

      // Init select.
      if (this.refs[filterRef]) {
        const selectize = $(this.refs[filterRef]).selectize({
          wrapperClass:         'xComboWrapper',
          inputClass:           'xComboInput',
          dropdownClass:        'xComboOptions',
          dropdownContentClass: 'xComboOption',
          selectOnTab:          true,
          allowEmptyOption:     true,
        })[0].selectize;

        // Bind the current filter type to the handler.
        const handler = this.handleSelectChanged.bind(this, filterType);

        // Can't use React's event bindings for this :(
        selectize.on('change', handler);
      }
    });
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Elements.Toolbars.FilteringToolbar}
   */
  render() {
    this.selectFilters = [];

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Entry filter (if enabled).
    let _entry;
    if (this.props.filterConfig.entry.enabled) {
      const filterType = 'entry';
      let _entryControls = '';

      // Output filter using correct display mode.
      switch (this.props.filterConfig.entry.displayMode) {
        // Output as select/combo input.
        case Enums.FILTER_DISPLAY_MODE_SELECT:
        default:
          const labels = this.props.filterConfig.entry.data.filterLabels;

          // Loop over entry filters.
          _entryControls = Object.keys(this.props.filterConfig.entry.data.filters).map((filter, fIndex) => {
            const filterKey = filter;
            const filterValue = this.props.filterConfig.entry.data.filters[filterKey];

            // Only output if it has at least one value.
            let _filterControl;
            if (this.props.filterConfig.entry.data.filterValues[filterKey].length > 0) {
              const _entryFilters = this.props.filterConfig.entry.data.filterValues[filterKey].map(function(
                filterDataValue
              ) {
                // Get the label for this value (if defined).
                let label = filterDataValue;
                if (labels[filterDataValue]) {
                  label = labels[filterDataValue];
                }

                return {
                  value: filterDataValue,
                  label: label,
                  icon:  null,
                };
              });
              _entryFilters.unshift({ value: 'all', label: filterValue + ': ' + getLabel('ugc.filters.all') });

              const _filterSelect = this.renderAsSelect(filterKey, _entryFilters);

              _filterControl = (
                <div key={fIndex} className="xFilterWrapper xEntry">
                  {_filterSelect}
                </div>
              );
            }

            return _filterControl;
          });

          break;
      }

      _entry = _entryControls;
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Network filter (if enabled).
    let _network;
    if (this.props.filterConfig.network.enabled) {
      const filterType = 'network';
      let _networkControls = '';

      // Output filter using correct display mode.
      switch (this.props.filterConfig.network.displayMode) {
        // Output as select/combo.
        case Enums.FILTER_DISPLAY_MODE_SELECT:
          _networkControls = this.renderAsSelect(filterType, this.networkConfig);

          break;

        // Output as buttons.
        case Enums.FILTER_DISPLAY_MODE_BUTTON:
        default:
          _networkControls = this.renderAsButtons(filterType, this.networkConfig);

          break;
      }

      const _label = this.renderLabel('ugc.filters.network.label');
      _network = (
        <div key={filterType} className="xFilterWrapper xNetwork">
          {_label}
          {_networkControls}
        </div>
      );
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Tag filter (if enabled).
    let _tag;
    if (this.props.filterConfig.tag.enabled) {
      const filterType = 'tag';
      let _tagControls = '';

      // Output filter using correct display mode.
      switch (this.props.filterConfig.tag.displayMode) {
        // Output as text input.
        case Enums.FILTER_DISPLAY_MODE_TEXTINPUT:
          _tagControls = this.renderAsTextInput(filterType, getLabel('ugc.filters.tag.placeholder'));

          break;

        // Output as buttons.
        case Enums.FILTER_DISPLAY_MODE_BUTTON:
        default:
          const _tagFilters = this.props.filterConfig.tag.data.tags.map(function(tag) {
            return {
              value: tag,
              label: tag,
              icon:  null,
            };
          });
          _tagFilters.unshift({ value: 'all', label: getLabel('ugc.filters.tag.all') });

          _tagControls = this.renderAsButtons(filterType, _tagFilters);

          break;
      }

      const _label = this.renderLabel('ugc.filters.tag.label');
      _tag = (
        <div key={filterType} className="xFilterWrapper xTag">
          {_label}
          {_tagControls}
        </div>
      );
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // User filter (if enabled).
    let _user;
    if (this.props.filterConfig.user.enabled) {
      const filterType = 'user';
      let _userControls = '';

      // Output filter using correct display mode.
      switch (this.props.filterConfig.user.displayMode) {
        // Output as text input.
        case Enums.FILTER_DISPLAY_MODE_TEXTINPUT:
        default:
          _userControls = this.renderAsTextInput(filterType, getLabel('ugc.filters.user.placeholder'));

          break;
      }

      const _label = this.renderLabel('ugc.filters.user.label');
      _user = (
        <div key={filterType} className="xFilterWrapper xUser">
          {_label}
          {_userControls}
        </div>
      );
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Content type filter (if enabled).
    let _type;
    if (this.props.filterConfig.type.enabled) {
      const filterType = 'type';
      let _typeControls = '';

      // Output filter using correct display mode.
      switch (this.props.filterConfig.type.displayMode) {
        // Output as select/combo input.
        case Enums.FILTER_DISPLAY_MODE_SELECT:
        default:
          _typeControls = this.renderAsSelect(filterType, this.contentTypeConfig);

          break;
      }

      const _label = this.renderLabel('ugc.filters.type.label');
      _type = (
        <div key={filterType} className="xFilterWrapper xType">
          {_label}
          {_typeControls}
        </div>
      );
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Date filter (if enabled).
    let _date;
    if (this.props.filterConfig.date.enabled) {
      const filterType = 'date';
      let _dateControls = '';

      // Output filter using correct display mode.
      switch (this.props.filterConfig.date.displayMode) {
        // Output as date picker widget.
        case Enums.FILTER_DISPLAY_MODE_DATE:
        default:
          // TODO
          _dateControls = '[date picker]';

          break;
      }

      const _label = this.renderLabel('ugc.filters.date.label');
      _date = (
        <div key={filterType} className="xFilterWrapper xDate">
          {_label}
          {_dateControls}
        </div>
      );
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Define classes for container.
    let _containerClasses = 'xActionContainer xActionFiltersContainer';

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Map filters to columns.
    let _cols;

    const _filtersToColsMap = [[_tag], [_network], [_type, _user, _entry]];

    _cols = _filtersToColsMap.map(function(colMap, index) {
      const _colIndex = index + 1;

      let _colClasses = 'xCol xCol' + _colIndex;

      const _hasFilters = colMap.join('') ? true : false;
      if (_hasFilters) {
        _containerClasses += ' xCol' + _colIndex + 'Filters';
      } else {
        _colClasses += ' xNoFilters';
      }

      const _col = (
        <div key={index} className={_colClasses}>
          {colMap}
        </div>
      );

      return _col;
    });

    return <div className={_containerClasses}>{_cols}</div>;
  }

  /**
   * Render controls as a select element.
   *
   * @param  {String} filterType The filter type identifier (eg. network|user|date etc)
   * @param  {Array} filters Array containg a list of filter objects to output.
   * @return {ReactElement} The filtering select elements.
   */
  renderAsSelect(filterType, filters) {
    let _selectControls;

    // Define ref.
    const _ref = filterType + 'Filter';

    // Loop over list of filters and generate options markup.
    const _selectOptions = filters.map((filter, index) => {
      return (
        <option key={index} value={filter.value}>
          {filter.label}
        </option>
      );
    });

    _selectControls = <select ref={_ref}>{_selectOptions}</select>;

    // Store reference to this select filter so that we can apply selectize to it on component mount.
    this.selectFilters.push({ ref: _ref, type: filterType });

    return _selectControls;
  }

  /**
   * Render controls as a series of buttons.
   *
   * @param  {String} filterType The filter type identifier (eg. network|user|date etc)
   * @param  {Array} filters Array containg a list of filter objects to output.
   * @return {ReactElement} The filtering button elements.
   */
  renderAsButtons(filterType, filters) {
    let _buttonControls;

    // Loop over list of filters and generate button markup.
    const _filterLi = filters.map((filter, index) => {
      // Bind the current filter object to the click handler.
      const _handler = this.handleButtonClicked.bind(this, filterType, filter.value);
      const _label = !filter.icon ? filter.label : null;
      const _icon = 'xIcon ' + filter.icon;
      const _classes = 'xButton xFilterItems xFilter' + filterType;

      return (
        <li key={index} className="xAction xActionFilter">
          <HybridButton key={index} onClick={_handler} className={_classes} iconClass={_icon}>
            {_label}
          </HybridButton>
        </li>
      );
    });

    _buttonControls = <ul className="xToolbar xButtonToolbar xActionFilterButtons">{_filterLi}</ul>;

    return _buttonControls;
  }

  /**
   * Render controls as a text entry field.
   *
   * @param  {String} filterType The filter type identifier (eg. network|user|date etc)
   * @param  {String} placeholder Placeholder text to show before user input.
   * @return {ReactElement} The filtering input element.
   */
  renderAsTextInput(filterType, placeholder) {
    const _handler = this.handleTextInputChangeBuffer.bind(this, filterType);

    const _textInputControls = <input type="text" placeholder={placeholder} onChange={_handler} />;

    return _textInputControls;
  }

  /**
   * Render a label element, given a label path.
   *
   * @param  {String} labelPath Path to the required localisation label.
   * @return {ReactElement} The label element.
   */
  renderLabel(labelPath) {
    let _label;
    const _labelValue = getLabel(labelPath);

    if (_labelValue) {
      _label = <label className="xFieldLabel">{_labelValue}:</label>;
    }

    return _label;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when a button is clicked.
   *
   * @param  {String} filterType The filter type identifier (eg. network|user|date etc)
   * @param  {String} filterValue The string value of the filter to apply.
   */
  handleButtonClicked(filterType, filterValue) {
    const filter = {};
    filter[filterType] = filterValue;

    this.applyFilters(filter);
  }

  /**
   * Handler called when text is entered by the user, which is buffered briefly before being processed further.
   *
   * @param  {String} filterType The filter type identifier (eg. network|user|date etc)
   * @param  {Object} event The input change event.
   */
  handleTextInputChangeBuffer(filterType, event) {
    const filterValue = event.target.value;

    if (this.timeouts[filterType]) {
      clearTimeout(this.timeouts[filterType]);
    }

    this.timeouts[filterType] = setTimeout(
      this.handleTextInputChanged.bind(this, filterType, filterValue),
      Config.filtering.userInputBuffer
    );
  }

  /**
   * Handler called when text is entered by the user, after the buffer time has passed.
   *
   * @param  {String} filterType The filter type identifier (eg. network|user|date etc)
   * @param  {String} filterValue The string value of the filter to apply.
   */
  handleTextInputChanged(filterType, filterValue) {
    const filter = {};
    filter[filterType] = filterValue;

    this.applyFilters(filter);
  }

  /**
   * Handler called when a select option changes.
   *
   * @param  {String} filterType The filter type identifier (eg. network|user|date etc)
   * @param  {String} filterValue The string value of the filter to apply.
   */
  handleSelectChanged(filterType, filterValue) {
    const filter = {};
    filter[filterType] = filterValue;

    this.applyFilters(filter);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Action dispatcher proxy methods */

  /**
   * Attempt to call the injected filter handler method.
   *
   * @param {Object} filters Object containg filters to apply.
   */
  applyFilters(filters) {
    if (this.props.clickHandlers.filter) {
      this.props.clickHandlers.filter(filters);
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Object} filterConfig Object containing configuration data for the filters required.
 * @property {Object} activeFilters Object containing current filter state.
 * @property {Object} clickHandlers Object containing event handlers for various events.
 */
FilteringToolbar.propTypes = {
  filterConfig:  PropTypes.object,
  activeFilters: PropTypes.object,
  clickHandlers: PropTypes.object,
};

/**
 * React default props.
 *
 * @type {Object}
 */
FilteringToolbar.defaultProps = {};

export default FilteringToolbar;
